import React, { useCallback, useEffect, useRef, useState } from 'react';

import userApi from '@editor/api/user';
import monitoring from '@editor/services/monitoring';
import { userStoreSelector, useUserStore } from '@editor/stores/userStore';
import { TransformedUseCase } from '@editor/types';
import { OnboardingState } from '@repo/domain/user';
import {
  onboardingStoreSelector,
  useOnboardingStore,
  BASE_ONBOARDING,
} from '@repo/onboarding/onboarding-store';
import {
  CreateFirst,
  type CreateFirstProps,
  HearAboutUs,
  WhatDescribesYou,
  WhatDescribesYourExperience,
} from '@repo/onboarding/onboarding-survey';

import OnboardingSurveyScreen from './OnboardingSurveyScreen';
import { SurveryAnswerProps } from './types';

export interface OnboardingModalsProps {
  onFinish?: CreateFirstProps['onAnswer'];
  enabled?: boolean;
  filteredUseCases?: TransformedUseCase[];
}

const OnboardingModals: React.FC<OnboardingModalsProps> = ({
  onFinish,
  enabled = true,
  filteredUseCases,
}) => {
  const selectedUseCaseRef = useRef('');
  const [isLoading, setIsLoading] = useState(false);

  const updateUserInfo = useUserStore(userStoreSelector.updateUserInfo);

  const step = useOnboardingStore(onboardingStoreSelector.step);
  const setNextStep = useOnboardingStore(onboardingStoreSelector.setNextStep);
  const setStep = useOnboardingStore(onboardingStoreSelector.setStep);
  const displayStep = useOnboardingStore(onboardingStoreSelector.displayStep);
  const init = useOnboardingStore(onboardingStoreSelector.init);

  const user = useUserStore(userStoreSelector.user);

  const handleUserReturnToTheTab = useCallback(async () => {
    if (document.visibilityState !== 'visible' || isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      const user = await updateUserInfo();

      if (!user) {
        return;
      }

      if (user.onboardingState !== step?.name) {
        await init(
          {
            step: BASE_ONBOARDING[user.onboardingState as OnboardingState],
            userId: user?.id,
          },
          true
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [updateUserInfo, step, init, isLoading]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleUserReturnToTheTab);

    return (): void => {
      document.removeEventListener(
        'visibilitychange',
        handleUserReturnToTheTab
      );
    };
  }, [handleUserReturnToTheTab]);

  const submitQuestionnaire = async (
    questionKey: OnboardingState,
    answerText: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await userApi.answerQuestionnaire({
        questionKey,
        answerText,
      });

      if (!response.success) {
        throw new Error(response.error);
      }
    } catch (error) {
      monitoring.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const showModal = !!user && user?.confirmed && !!displayStep && enabled;

  const getShowModal = (step: OnboardingState): boolean => {
    return showModal && displayStep?.name === step;
  };

  const handleSurveyAnswer = async ({
    onboardingState,
    answerText,
  }: SurveryAnswerProps): Promise<void> => {
    try {
      if (onboardingState && answerText) {
        await submitQuestionnaire(onboardingState, answerText);
      }

      if (onboardingState === OnboardingState.CreateFirst) {
        selectedUseCaseRef.current = answerText;
      }

      await setNextStep();
    } catch (error) {
      return;
    }
  };

  const handleCreateFirstAnswer: CreateFirstProps['onAnswer'] = async (
    selection
  ): Promise<void> => {
    try {
      await submitQuestionnaire(OnboardingState.CreateFirst, selection.value);

      // Skip upgrade modal
      void setStep(BASE_ONBOARDING.SURVEY);

      onFinish?.(selection);
    } catch (error) {
      return;
    }
  };

  const showScreen = showModal && displayStep?.isSurvey;

  if (!showScreen) {
    return null;
  }

  return (
    <OnboardingSurveyScreen>
      {getShowModal(OnboardingState.WhatDescribesYou) && (
        <WhatDescribesYou
          isDisabled={isLoading}
          onAnswer={handleSurveyAnswer}
        />
      )}
      {getShowModal(OnboardingState.HearAboutUs) && (
        <HearAboutUs isDisabled={isLoading} onAnswer={handleSurveyAnswer} />
      )}
      {getShowModal(OnboardingState.HowExperienced) && (
        <WhatDescribesYourExperience
          isDisabled={isLoading}
          onAnswer={handleSurveyAnswer}
        />
      )}
      {getShowModal(OnboardingState.CreateFirst) && (
        <CreateFirst
          isDisabled={isLoading}
          useCases={filteredUseCases}
          onAnswer={handleCreateFirstAnswer}
        />
      )}
    </OnboardingSurveyScreen>
  );
};

export default OnboardingModals;
